import { useEffect } from 'react';
import { Font } from '@react-pdf/renderer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import publicSansBold from 'src/assets/fonts/PublicSans-Bold.ttf';
import publicSansItalic from 'src/assets/fonts/PublicSans-Italic.ttf';
import publicSansRegular from 'src/assets/fonts/PublicSans-Regular.ttf';

import { AuthProvider } from './auth';
import { Snackbar } from './components/snackbar';
import { useScrollToTop } from './hooks/useScrollToTop';
import { HistoryProvider } from './routes/context/HistoryContext';

// ------------------------ PDF Configs --------------------------------
Font.register({
  family: 'Public Sans',
  fonts: [
    {
      src: publicSansRegular,
    },
    {
      src: publicSansBold,
      fontWeight: 700,
    },
    {
      src: publicSansItalic,
      fontStyle: 'italic',
    },
  ],
});

// ------------------------ Tanstack Query Client --------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
    },
  },
});

function App({ children }: { children: React.ReactNode }) {
  useScrollToTop();

  useEffect(() => {
    const dynatraceUrl = import.meta.env.VITE_DYNATRACE_URL;
    if (dynatraceUrl) {
      // For local test only as it'll only inject once during build time
      const existingScript = document.querySelector(`script[src="${dynatraceUrl}"]`);
      if (existingScript) {
        console.log('Dynatrace script already exists, skipping injection');
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = dynatraceUrl;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Snackbar />
        <HistoryProvider>{children}</HistoryProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
